import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { usePayrollAdjustments, usePayrollTax } from "../../lib/api";
import { Money, SectionCard, Stat } from "../labour/shared";

/** Corrections, and what IPR actually charges (HRX-07).
 *
 * `Additional Salary` held zero rows across six months and a hundred people. That is not a clean payroll; it is
 * a payroll with no mechanism for being wrong. Nothing here edits a submitted slip — six months are closed and
 * their figures are what people were actually paid, so a correction is a document of its own with its own date
 * and its own reason.
 *
 * The tax panel states its basis on the face of it, because getting this the wrong way round is not a small
 * error: **the IPR bands are annual and pay is monthly**. Applying the annual scale to one month's gross puts a
 * senior salary two bands too low and produced, on a first pass here, a computed charge 2.4x under what payroll
 * deducts — which would have read as a 40 M CDF over-charge that does not exist. */
export default function Adjustments({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = usePayrollAdjustments(company || undefined);
  const tax = usePayrollTax(company || undefined);

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;

  return (
    <div className="mt-4 space-y-4" data-payroll-adjustments>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-payroll-adj-strip>
        <Stat label={t("pay.corrections")} value={d.count} hint={t("pay.correctionsHint")} />
        <Stat label={t("pay.correctionValue")} value={<Money value={d.total} lng={lng} />} />
        <Stat label={t("pay.earningsAdded")} value={<Money value={d.by_type.Earning ?? 0} lng={lng} />} cls="text-status-green" />
        <Stat label={t("pay.deductionsAdded")} value={<Money value={d.by_type.Deduction ?? 0} lng={lng} />} cls="text-status-amber" />
      </div>

      <SectionCard title={t("pay.correctionLog")} aside={t("pay.correctionLogHint")}>
        {d.rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-payroll-adj-empty>{t("pay.noCorrections")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
              <tr>
                <th className="px-4 py-2">{t("wfa.person")}</th>
                <th className="px-2 py-2">{t("pay.onRun")}</th>
                <th className="px-2 py-2">{t("pay.component")}</th>
                <th className="px-4 py-2">{t("pay.why")}</th>
                <th className="px-4 py-2 text-right">{t("pay.amount")}</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.name} className="border-t border-brand-grey-light" data-payroll-adjustment={r.name}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{r.employee_name}</div>
                    <div className="text-[11px] text-brand-grey">{r.company}</div>
                  </td>
                  <td className="px-2 py-2 tabular-nums">{r.payroll_date}</td>
                  <td className="px-2 py-2">
                    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${
                      r.component_type === "Deduction" ? "bg-amber-50 text-status-amber" : "bg-green-50 text-status-green"}`}>
                      {r.salary_component}
                    </span>
                  </td>
                  <td className="max-w-md px-4 py-2 text-[12px] text-brand-grey">{r.reason || <span className="italic">{t("pay.noReason")}</span>}</td>
                  <td className="px-4 py-2 text-right font-semibold"><Money value={r.amount} lng={lng} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      {tax.data?.slab && (
        <SectionCard title={t("pay.tax")} aside={tax.data.slab.name}>
          <div className="p-4">
            <p className="max-w-4xl text-sm text-brand-grey" data-payroll-tax-basis>
              {t("pay.taxBasis", { months: tax.data.basis?.months_per_year ?? 12 })}
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px]" data-payroll-tax-bands>
              {tax.data.bands.map((b, i) => (
                <span key={i} className="rounded border border-brand-grey-light px-2 py-1">
                  {t("pay.band", { from: Math.round(b.from_amount / 1e6), to: b.to_amount ? Math.round(b.to_amount / 1e6) : "…", rate: b.percent_deduction })}
                </span>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4" data-payroll-tax-summary>
              <Fact label={t("pay.charged")} value={<Money value={tax.data.summary.charged} lng={lng} />} />
              <Fact label={t("pay.recomputed")} value={<Money value={tax.data.summary.computed} lng={lng} />} />
              <Fact label={t("pay.residual")} value={`${tax.data.summary.residual_pct} %`} />
              <Fact label={t("pay.relief")} value={tax.data.summary.relief_applied ? t("pay.applied") : t("pay.none")}
                tone={tax.data.summary.relief_applied ? undefined : "amber"} />
            </div>
            <p className="mt-3 max-w-4xl text-[11px] text-brand-grey">{t("pay.residualNote")}</p>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

function Fact({ label, value, tone }: { label: string; value: React.ReactNode; tone?: "amber" }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-brand-grey">{label}</div>
      <div className={`font-heading text-lg font-semibold ${tone === "amber" ? "text-status-amber" : ""}`}>{value}</div>
    </div>
  );
}
